import { useState, useEffect, useMemo, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import {
  STUDENT_TIERS,
  DEFAULT_STUDENT_TIER,
  STUDENT_TIER_STORAGE_KEY,
  resolveStudentTier,
  getStoredStudentTier,
  setStoredStudentTier,
  clearStoredStudentTier,
} from '../lib/studentTierResolver';

/**
 * خطاف مخصص لاستخراج وتحديد فئة ومرحلة الطالب التعليمية ديناميكياً
 * مع دعم التخزين المحلي (Local Storage) للتحميل الفوري وتفادي أي وميض.
 * 
 * المستويات المعتمدة (3 Tiers):
 * 1. kids: فئة الصغار (الأعمار من 1 إلى 11 سنة / الروضة والصفوف من 1 إلى 5).
 * 2. teen: فئة اليافعين (الأعمار من 12 إلى 16 سنة / الصفوف من 6 إلى 10).
 * 3. senior: فئة الكبار (الأعمار 17 سنة فما فوق / الصفوف 11 و12 والجامعة).
 * 
 * المخرجات:
 * - tier: اسم الفئة ('kids' | 'teen' | 'senior')
 * - isKids: boolean (true للأعمار من 1 إلى 11)
 * - isTeen: boolean (true للأعمار من 12 إلى 16)
 * - isSenior: boolean (true للأعمار 17+)
 * - gradeCode: رمز الصف (مثل 'G1' أو 'G10')
 * - gradeName: اسم الصف المعرف لدى الطالب
 * - setTier: دالة لتغيير وتحديد الفئة يدوياً للتجربة والاختبار
 * - resetTier: دالة لإعادة الاحتساب التلقائي ومزامنة الحساب
 */
export function useStudentTier() {
  const { user, isAuthenticated } = useAuth();

  const studentProfile = user?.role_profile?.student;
  const gradeName = studentProfile?.grade_name || null;
  
  // ─── قراءة رمز الصف الفعلي والمباشر القادم من الخادم ───
  const gradeCode = studentProfile?.grade_code || studentProfile?.grade?.code || user?.grade_code || null;
  
  const age = user?.age ?? null;
  const birthDate = user?.birth_date || user?.birthDate || studentProfile?.birth_date || null;

  const role = (user?.role || user?.roles?.[0])?.toLowerCase();
  const isAdmin = role === 'admin' || role === 'superadmin' || user?.is_superuser;
  const isGuest = !isAdmin && (role === 'guest' || Boolean(user?.is_guest));

  // احتساب الفئة المحسوبة من بيانات الحساب الفعلي
  const computedTier = useMemo(() => {
    return resolveStudentTier({
      gradeCode,
      gradeName,
      age,
      birthDate,
      role,
      isGuest,
    });
  }, [gradeCode, gradeName, age, birthDate, role, isGuest]);

  // حالة التغيير اليدوي (تُستخدم فقط عند استدعاء setTier يدوياً للتجربة)
  const [manualTier, setManualTier] = useState(null);

  // الفئة الفعلية المعتمدة (المسؤولون في الفئة العليا، والباقي بناءً على العمر/الصف المحسوب)
  const tier = isAdmin
    ? (manualTier || STUDENT_TIERS.SENIOR)
    : (manualTier || computedTier || getStoredStudentTier() || DEFAULT_STUDENT_TIER);

  // مزامنة التخزين المحلي دائماً مع الفئة الفعلية
  useEffect(() => {
    if (tier) {
      setStoredStudentTier(tier);
    }
  }, [tier]);

  // مسح التخزين عند تسجيل الخروج
  useEffect(() => {
    const handleTokenChange = (e) => {
      if (!e.detail) {
        clearStoredStudentTier();
        setManualTier(null);
      }
    };

    window.addEventListener('token_changed', handleTokenChange);
    return () => window.removeEventListener('token_changed', handleTokenChange);
  }, []);

  // دالة لتغيير الفئة يدوياً للاختبار
  const setTier = useCallback((newTier) => {
    if (newTier && Object.values(STUDENT_TIERS).includes(newTier)) {
      setManualTier(newTier);
      setStoredStudentTier(newTier);
      console.log(`%c 🔄 [تغيير يدوي للفئة]: تم تحويل الفئة إلى [${newTier.toUpperCase()}]`, 'color: #8b5cf6; font-weight: bold;');
    }
  }, []);

  // دالة لإعادة ضبط الفئة حسب بيانات الحساب
  const resetTier = useCallback(() => {
    setManualTier(null);
    setStoredStudentTier(computedTier);
  }, [computedTier]);

  // طباعة مباشرة وواضحة جداً في شاشة المطورين
  useEffect(() => {
    if (isAuthenticated) {
      const tierBadgeColor = tier === STUDENT_TIERS.KIDS ? '#f59e0b' : tier === STUDENT_TIERS.TEEN ? '#8b5cf6' : '#10b981';
      console.log(
        `%c 🎓 فئة الطالب: [ ${tier.toUpperCase()} ] %c (رمز الصف: "${gradeCode || 'غير محدد'}" | اسم الصف: "${gradeName || 'غير محدد'}")`,
        `background: ${tierBadgeColor}; color: #ffffff; font-weight: 900; font-size: 14px; padding: 5px 10px; border-radius: 6px;`,
        'color: #061542; font-weight: bold; font-size: 13px; margin-left: 6px;',
        {
          tier,
          gradeCode,
          gradeName,
          age,
          birthDate,
          isKids: tier === STUDENT_TIERS.KIDS,
          isTeen: tier === STUDENT_TIERS.TEEN,
          isJunior: tier === STUDENT_TIERS.TEEN,
          isSenior: tier === STUDENT_TIERS.SENIOR,
          manualOverrideActive: !!manualTier,
        }
      );
    }
  }, [isAuthenticated, tier, gradeCode, gradeName, age, birthDate, manualTier]);

  return {
    tier,
    isKids: tier === STUDENT_TIERS.KIDS,
    isTeen: tier === STUDENT_TIERS.TEEN,
    isJunior: tier === STUDENT_TIERS.TEEN,
    isSenior: tier === STUDENT_TIERS.SENIOR,
    gradeCode,
    gradeName,
    age,
    birthDate,
    setTier,
    resetTier,
    user,
  };
}

export default useStudentTier;
